import React, { useState, useEffect } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Textarea, Label } from "./ui/input";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "./ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { ShieldCheck, Loader2, History, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" }) : "—");

export function FactorVerification({ canEdit }) {
  const [streams, setStreams] = useState([]);
  const [options, setOptions] = useState([]);
  const [hist, setHist] = useState([]);
  const [target, setTarget] = useState(null);
  const [mode, setMode] = useState("confirm");
  const [factorCode, setFactorCode] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, h] = await Promise.all([api.get("/factor-verify/waste-streams"), api.get("/factor-verify/history")]);
      setStreams(s.data.streams || []); setOptions(s.data.factor_options || []); setHist(h.data.history || []);
    } catch { /* read-only failure tolerated */ }
  };
  useEffect(() => { load(); }, []);

  const openVerify = (s) => { setTarget(s); setMode("confirm"); setFactorCode(s.factor?.code || ""); setNote(""); };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/factor-verify/waste-streams/verify", {
        activity_type: target.activity_type, action: mode,
        factor_code: mode === "override" ? factorCode : undefined, note: note || undefined,
      });
      toast.success(mode === "override"
        ? `Factor overridden — ${data.records_affected} record(s) remapped and emissions recalculated`
        : "Disposal method confirmed — sign-off recorded");
      setTarget(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
    finally { setBusy(false); }
  };

  const statusBadge = (v) => {
    if (!v) return <Badge variant="warning" className="text-[10px]" data-testid="factor-status-unverified"><AlertTriangle className="h-2.5 w-2.5 mr-1" />Unverified</Badge>;
    return v.action === "override"
      ? <Badge variant="default" className="text-[10px]" data-testid="factor-status-overridden">Overridden · {fmtDate(v.verified_at)}</Badge>
      : <Badge variant="lime" className="text-[10px]" data-testid="factor-status-confirmed"><ShieldCheck className="h-2.5 w-2.5 mr-1" />Confirmed · {fmtDate(v.verified_at)}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="card-flat p-6">
        <h2 className="font-display font-semibold text-lg mb-1">Waste disposal factor sign-off</h2>
        <p className="text-xs text-muted-foreground mb-4 max-w-3xl">
          Each waste stream's emissions depend on its disposal route (recycling, incineration, composting, landfill).
          Confirm the factor applied to each stream — or override it — to give auditors a documented sign-off trail.
        </p>
        <Table data-testid="factor-verify-table">
          <TableHeader>
            <TableRow>
              <TableHead>Waste stream</TableHead>
              <TableHead className="text-right">Records</TableHead>
              <TableHead className="text-right">Volume</TableHead>
              <TableHead>Current factor</TableHead>
              <TableHead className="text-right">Emissions</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {streams.map((s) => (
              <TableRow key={s.activity_type} data-testid={`factor-stream-${s.activity_type}`}>
                <TableCell className="font-medium text-sm">{s.activity_type}</TableCell>
                <TableCell className="text-right font-mono text-xs">{s.records}</TableCell>
                <TableCell className="text-right font-mono text-xs">{s.total_value.toLocaleString()} {s.unit}</TableCell>
                <TableCell className="text-xs">
                  {s.factor ? <>{s.factor.name} <span className="text-muted-foreground font-mono">({s.factor.factor_kgco2e} kgCO₂e/t)</span></> : <span className="text-muted-foreground">—</span>}
                  {s.factor?.registry && (
                    <Badge variant={s.factor.registry === "SEFR" ? "lime" : "outline"} className="text-[9px] ml-1.5 align-middle"
                      data-testid="factor-registry-badge"
                      title={`${s.factor.source || ""}${s.factor.ipcc_version ? " · " + s.factor.ipcc_version : ""}`}>
                      {s.factor.registry === "SEFR" ? "SEFR·SG" : "IPCC·intl"}{s.factor.ipcc_version ? ` · ${s.factor.ipcc_version}` : ""}
                    </Badge>
                  )}
                  {s.mixed_factors && <span className="text-amber-400 ml-1">(mixed)</span>}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">{s.co2e_tonnes} tCO₂e</TableCell>
                <TableCell>{statusBadge(s.verification)}</TableCell>
                <TableCell className="text-right">
                  {canEdit && <Button variant="outline" size="sm" className="h-7" onClick={() => openVerify(s)} data-testid={`verify-factor-btn-${s.activity_type}`}>Verify…</Button>}
                </TableCell>
              </TableRow>
            ))}
            {!streams.length && <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">No waste streams recorded yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <div className="card-flat p-6" data-testid="factor-signoff-trail">
        <h3 className="font-display font-semibold text-sm mb-3 inline-flex items-center gap-2"><History className="h-4 w-4 text-accent" /> Sign-off trail</h3>
        {hist.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Stream</TableHead><TableHead>Action</TableHead>
                <TableHead>Factor</TableHead><TableHead className="text-right">Records</TableHead>
                <TableHead>By</TableHead><TableHead>Note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {hist.map((v) => (
                <TableRow key={v.id} data-testid={`signoff-row-${v.id}`}>
                  <TableCell className="font-mono text-xs whitespace-nowrap">{fmtDate(v.verified_at)}</TableCell>
                  <TableCell className="text-xs">{v.activity_type}</TableCell>
                  <TableCell>{v.action === "override" ? <Badge variant="default" className="text-[9px]">Override</Badge> : <Badge variant="lime" className="text-[9px]">Confirm</Badge>}</TableCell>
                  <TableCell className="text-xs">
                    {v.action === "override" && v.previous_factor_codes?.length ? <span className="text-muted-foreground font-mono">{v.previous_factor_codes.join(", ")} → </span> : null}
                    <span className="font-mono">{v.factor_code}</span> <span className="text-muted-foreground">({v.factor_kgco2e} kg/t)</span>
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">{v.records_affected}</TableCell>
                  <TableCell className="text-xs">{v.verified_by_name || v.verified_by}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[220px] truncate" title={v.note || ""}>{v.note || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : <p className="text-sm text-muted-foreground">No sign-offs recorded yet.</p>}
      </div>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent data-testid="factor-verify-dialog">
          <DialogHeader><DialogTitle>Verify disposal factor — {target?.activity_type}</DialogTitle></DialogHeader>
          {target && (
            <div className="space-y-4">
              <div className="card-flat p-3 text-xs space-y-1">
                <div>{target.records} record(s) · {target.total_value.toLocaleString()} {target.unit} · <span className="font-mono">{target.co2e_tonnes} tCO₂e</span></div>
                <div className="text-muted-foreground">Current: {target.factor ? `${target.factor.name} (${target.factor.factor_kgco2e} kgCO₂e/t)` : "no factor applied"}</div>
                {target.factor?.source && <div className="text-[10px] text-muted-foreground">{target.factor.source}</div>}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setMode("confirm")} data-testid="verify-mode-confirm"
                  className={`flex-1 text-xs px-3 py-2 rounded border transition-colors ${mode === "confirm" ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:border-accent/60"}`}>
                  Confirm current method
                </button>
                <button type="button" onClick={() => setMode("override")} data-testid="verify-mode-override"
                  className={`flex-1 text-xs px-3 py-2 rounded border transition-colors ${mode === "override" ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:border-accent/60"}`}>
                  Override disposal method
                </button>
              </div>
              {mode === "override" && (
                <div>
                  <Label>New disposal factor</Label>
                  <Select value={factorCode} onValueChange={setFactorCode}>
                    <SelectTrigger data-testid="override-factor-select"><SelectValue placeholder="Select disposal route…" /></SelectTrigger>
                    <SelectContent>
                      {options.map((f) => <SelectItem key={f.code} value={f.code}>{f.name} — {f.factor_kgco2e} kgCO₂e/t</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-amber-400 mt-1.5">⚠ All {target.records} record(s) in this stream will be remapped and emissions recalculated.</p>
                </div>
              )}
              <div>
                <Label>Sign-off note {mode === "confirm" ? "(basis for confirmation)" : "(reason for override)"}</Label>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="e.g. Disposal contractor confirmed 100% of C&D waste goes to licensed recycling facility." data-testid="verify-note-input" />
              </div>
              <Button variant="primary" className="w-full" onClick={submit} data-testid="verify-submit-btn"
                disabled={busy || (mode === "override" && (!factorCode || factorCode === target.factor?.code))}>
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Recording…</> : mode === "confirm" ? <><ShieldCheck className="h-4 w-4" /> Record confirmation</> : "Override & recalculate"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
